type OnError = (error: string) => void;
type OnConnected = () => void;
type TelemetryEmit = (event: string, details?: Record<string, unknown>) => void;

interface OfferData {
	iceUfrag: string;
	icePwd: string;
	medias: string[];
}

export interface ReconnectStrategyConfig {
	/**
	 * How long the peer is allowed to sit in `connectionState='disconnected'`
	 * before we treat it as a real failure. Lets ICE consistency checks recover
	 * transient blips without tearing down the WHIP session.
	 */
	disconnectedHysteresisMs?: number;

	/**
	 * Cap on awaiting the DELETE of the old WHIP session before posting a new
	 * offer. We await the DELETE so MediaMTX sees a clean notReady→ready
	 * cycle instead of "closing existing publisher". If the DELETE takes
	 * longer than this (bad network is usually why we got here), we proceed
	 * anyway — best effort.
	 */
	deleteTimeoutMs?: number;

	/**
	 * Per-attempt backoff in ms. Attempt N picks index min(N, length-1), so the
	 * last value is the steady-state cap once we've burned through the ramp.
	 */
	backoffMs?: number[];

	/**
	 * If we accumulate this many failures within `catastrophicWindowMs`, stop
	 * retrying and surface a terminal error so the UI can degrade gracefully.
	 */
	catastrophicFailuresThreshold?: number;
	catastrophicWindowMs?: number;

	/**
	 * After this much sustained 'connected' time, reset the attempt counter
	 * back to zero so the next failure starts from the shortest backoff.
	 */
	connectedResetThresholdMs?: number;
}

export interface MediaMTXWebRTCPublisherConfig {
	url: string;
	user?: string;
	pass?: string;
	token?: string;
	stream: MediaStream;
	videoCodec: string;
	videoBitrate: number;
	audioCodec: string;
	audioBitrate: number;
	audioVoice: boolean;
	reconnect?: ReconnectStrategyConfig;
	onError?: OnError;
	onConnected?: OnConnected;
	/**
	 * Optional telemetry emitter. When provided the publisher will call it
	 * for every meaningful state transition + HTTP exchange and once per
	 * `statsSampleIntervalMs` for an RTC stats snapshot while connected.
	 */
	telemetry?: TelemetryEmit;
	statsSampleIntervalMs?: number;
}

type PublisherState =
	| 'running'
	| 'hysteresis'
	| 'restarting'
	| 'catastrophic'
	| 'closed';

const DEFAULT_RECONNECT: Required<ReconnectStrategyConfig> = {
	disconnectedHysteresisMs: 5000,
	deleteTimeoutMs: 3000,
	backoffMs: [2000, 5000, 15000, 30000],
	catastrophicFailuresThreshold: 5,
	catastrophicWindowMs: 120000,
	connectedResetThresholdMs: 60000,
};

export class MediaMTXWebRTCPublisher {
	private readonly conf: MediaMTXWebRTCPublisherConfig;
	private readonly reconnect: Required<ReconnectStrategyConfig>;
	private state: PublisherState = 'running';
	private restartTimeout: number | null = null;
	private hysteresisTimeout: number | null = null;
	private connectedResetTimeout: number | null = null;
	private statsSamplerInterval: number | null = null;
	private attemptIndex = 0;
	private recentFailures: number[] = [];
	private pc: RTCPeerConnection | null = null;
	private offerData: OfferData | null = null;
	private sessionUrl: string | null = null;
	private mediamtxSessionId: string | null = null;
	private queuedCandidates: RTCIceCandidate[] = [];
	private lastPeerConnectionState: RTCPeerConnectionState | 'never' = 'never';

	constructor(conf: MediaMTXWebRTCPublisherConfig) {
		this.conf = conf;
		this.reconnect = MediaMTXWebRTCPublisher.resolveReconnectConfig(
			conf.reconnect
		);
		this.emit('publisher.created', { whip_url: conf.url });
		this.start();
	}

	/**
	 * Merge user-supplied reconnect overrides on top of the defaults using
	 * per-field fallback.
	 *
	 * Why not `{...DEFAULT_RECONNECT, ...userConfig}`?
	 * Callers (the Pinia store) explicitly pass each field by name, and
	 * when the server-side Inertia props omit one (e.g. `backoff_ms` isn't
	 * in the env block), the field arrives as `undefined`. A naive spread
	 * merges `undefined` over the default and the publisher ends up with
	 * no recovery curve at all — `backoffMs.length` then throws on the
	 * first restart attempt.
	 */
	private static resolveReconnectConfig(
		user: ReconnectStrategyConfig | undefined
	): Required<ReconnectStrategyConfig> {
		const r = user ?? {};
		return {
			disconnectedHysteresisMs:
				r.disconnectedHysteresisMs ??
				DEFAULT_RECONNECT.disconnectedHysteresisMs,
			deleteTimeoutMs:
				r.deleteTimeoutMs ?? DEFAULT_RECONNECT.deleteTimeoutMs,
			backoffMs: r.backoffMs ?? DEFAULT_RECONNECT.backoffMs,
			catastrophicFailuresThreshold:
				r.catastrophicFailuresThreshold ??
				DEFAULT_RECONNECT.catastrophicFailuresThreshold,
			catastrophicWindowMs:
				r.catastrophicWindowMs ??
				DEFAULT_RECONNECT.catastrophicWindowMs,
			connectedResetThresholdMs:
				r.connectedResetThresholdMs ??
				DEFAULT_RECONNECT.connectedResetThresholdMs,
		};
	}

	private emit(event: string, details: Record<string, unknown> = {}): void {
		try {
			this.conf.telemetry?.(event, details);
		} catch {
			// Telemetry must never break the publisher
		}
	}

	close = () => {
		this.emit('publisher.close', {
			final_attempt_index: this.attemptIndex,
		});
		this.state = 'closed';

		this.stopStatsSampler();

		if (this.pc !== null) {
			this.pc.close();
			this.pc = null;
		}

		if (this.restartTimeout !== null) {
			clearTimeout(this.restartTimeout);
			this.restartTimeout = null;
		}

		if (this.hysteresisTimeout !== null) {
			clearTimeout(this.hysteresisTimeout);
			this.hysteresisTimeout = null;
		}

		if (this.connectedResetTimeout !== null) {
			clearTimeout(this.connectedResetTimeout);
			this.connectedResetTimeout = null;
		}

		if (this.sessionUrl !== null) {
			fetch(this.sessionUrl, { method: 'DELETE' }).catch(() => {});
			this.sessionUrl = null;
		}
	};

	private static unquoteCredential(value: string): string {
		return JSON.parse(`"${value}"`);
	}

	private static linkToIceServers(links: string | null): RTCIceServer[] {
		return links !== null
			? links.split(', ').map((link) => {
					const match = link.match(
						/^<(.+?)>; rel="ice-server"(; username="(.*?)"; credential="(.*?)"; credential-type="password")?/i
					);

					if (match === null) {
						return { urls: [] };
					}

					const iceServer: RTCIceServer = { urls: [match[1]] };

					if (match[3] !== undefined) {
						iceServer.username =
							MediaMTXWebRTCPublisher.unquoteCredential(match[3]);
						iceServer.credential =
							MediaMTXWebRTCPublisher.unquoteCredential(match[4]);
					}

					return iceServer;
			  })
			: [];
	}

	private static parseOffer(offer: string): OfferData {
		const data: OfferData = {
			iceUfrag: '',
			icePwd: '',
			medias: [],
		};

		for (const line of offer.split('\r\n')) {
			if (line.startsWith('m=')) {
				data.medias.push(line.slice('m='.length));
			} else if (
				data.iceUfrag === '' &&
				line.startsWith('a=ice-ufrag:')
			) {
				data.iceUfrag = line.slice('a=ice-ufrag:'.length);
			} else if (data.icePwd === '' && line.startsWith('a=ice-pwd:')) {
				data.icePwd = line.slice('a=ice-pwd:'.length);
			}
		}

		return data;
	}

	private static generateSdpFragment(
		offerData: OfferData,
		candidates: RTCIceCandidate[]
	): string {
		const candidatesByMedia: Record<number, RTCIceCandidate[]> = {};
		for (const candidate of candidates) {
			const mid = candidate.sdpMLineIndex ?? 0;
			candidatesByMedia[mid] = candidatesByMedia[mid] ?? [];
			candidatesByMedia[mid].push(candidate);
		}

		let fragment =
			`a=ice-ufrag:${offerData.iceUfrag}\r\n` +
			`a=ice-pwd:${offerData.icePwd}\r\n`;

		let mid = 0;
		for (const media of offerData.medias) {
			if (candidatesByMedia[mid] !== undefined) {
				fragment += `m=${media}\r\n` + `a=mid:${mid}\r\n`;

				for (const candidate of candidatesByMedia[mid]) {
					fragment += `a=${candidate.candidate}\r\n`;
				}
			}
			mid++;
		}

		return fragment;
	}

	private static setCodec(section: string, codec: string): string {
		const lines = section.split('\r\n');
		const linesWithoutOtherCodecs: string[] = [];
		const payloadFormats: string[] = [];

		for (const line of lines) {
			if (!line.startsWith('a=rtpmap:')) {
				linesWithoutOtherCodecs.push(line);
			} else if (line.toLowerCase().includes(codec.toLowerCase())) {
				payloadFormats.push(
					line.slice('a=rtpmap:'.length).split(' ')[0]
				);
				linesWithoutOtherCodecs.push(line);
			}
		}

		const editedLines: string[] = [];
		let firstLine = true;

		for (const line of linesWithoutOtherCodecs) {
			if (firstLine) {
				firstLine = false;
				editedLines.push(
					line.split(' ').slice(0, 3).concat(payloadFormats).join(' ')
				);
			} else if (line.startsWith('a=fmtp:')) {
				if (
					payloadFormats.includes(
						line.slice('a=fmtp:'.length).split(' ')[0]
					)
				) {
					editedLines.push(line);
				}
			} else if (line.startsWith('a=rtcp-fb:')) {
				if (
					payloadFormats.includes(
						line.slice('a=rtcp-fb:'.length).split(' ')[0]
					)
				) {
					editedLines.push(line);
				}
			} else {
				editedLines.push(line);
			}
		}

		return editedLines.join('\r\n');
	}

	private static setVideoBitrate(section: string, bitrate: number): string {
		let lines = section.split('\r\n');

		for (let index = 0; index < lines.length; index++) {
			if (lines[index].startsWith('c=')) {
				lines = [
					...lines.slice(0, index + 1),
					`b=TIAS:${(bitrate * 1024).toString()}`,
					...lines.slice(index + 1),
				];
				break;
			}
		}

		return lines.join('\r\n');
	}

	private static setAudioBitrate(
		section: string,
		bitrate: number,
		voice: boolean
	): string {
		let opusPayloadFormat = '';
		const lines = section.split('\r\n');

		for (const line of lines) {
			if (
				line.startsWith('a=rtpmap:') &&
				line.toLowerCase().includes('opus/')
			) {
				opusPayloadFormat = line
					.slice('a=rtpmap:'.length)
					.split(' ')[0];
				break;
			}
		}

		if (opusPayloadFormat === '') {
			return section;
		}

		for (let index = 0; index < lines.length; index++) {
			if (!lines[index].startsWith(`a=fmtp:${opusPayloadFormat} `)) {
				continue;
			}

			lines[index] = voice
				? `a=fmtp:${opusPayloadFormat} minptime=10;useinbandfec=1;maxaveragebitrate=${(
						bitrate * 1024
				  ).toString()}`
				: `a=fmtp:${opusPayloadFormat} maxplaybackrate=48000;stereo=1;sprop-stereo=1;maxaveragebitrate=${(
						bitrate * 1024
				  ).toString()}`;
		}

		return lines.join('\r\n');
	}

	private static editOffer(
		sdp: string,
		videoCodec: string,
		audioCodec: string,
		audioBitrate: number,
		audioVoice: boolean
	): string {
		const sections = sdp.split('m=');

		for (let index = 0; index < sections.length; index++) {
			if (sections[index].startsWith('video')) {
				sections[index] = MediaMTXWebRTCPublisher.setCodec(
					sections[index],
					videoCodec
				);
			} else if (sections[index].startsWith('audio')) {
				sections[index] = MediaMTXWebRTCPublisher.setAudioBitrate(
					MediaMTXWebRTCPublisher.setCodec(
						sections[index],
						audioCodec
					),
					audioBitrate,
					audioVoice
				);
			}
		}

		return sections.join('m=');
	}

	private static editAnswer(sdp: string, videoBitrate: number): string {
		const sections = sdp.split('m=');

		for (let index = 0; index < sections.length; index++) {
			if (sections[index].startsWith('video')) {
				sections[index] = MediaMTXWebRTCPublisher.setVideoBitrate(
					sections[index],
					videoBitrate
				);
			}
		}

		return sections.join('m=');
	}

	private start() {
		this.requestIceServers()
			.then((iceServers) => this.setupPeerConnection(iceServers))
			.then((offer) => this.sendOffer(offer))
			.then((answer) => this.setAnswer(answer))
			.catch((error) => {
				this.handleError(error.toString());
			});
	}

	private async handleError(error: string) {
		// Only enter from running or hysteresis. Any other state means we're
		// already restarting, closed, or have given up.
		if (this.state !== 'running' && this.state !== 'hysteresis') {
			return;
		}

		this.emit('publisher.peer_state', {
			from: this.lastPeerConnectionState,
			to: 'failed',
			reason: error,
		});
		this.lastPeerConnectionState = 'failed';
		this.stopStatsSampler();

		// Cancel any pending recovery timers — handleError supersedes them.
		if (this.hysteresisTimeout !== null) {
			clearTimeout(this.hysteresisTimeout);
			this.hysteresisTimeout = null;
		}
		if (this.connectedResetTimeout !== null) {
			clearTimeout(this.connectedResetTimeout);
			this.connectedResetTimeout = null;
		}

		// Track failure timestamps for catastrophic-limit detection.
		const now = Date.now();
		this.recentFailures.push(now);
		this.recentFailures = this.recentFailures.filter(
			(ts) => ts >= now - this.reconnect.catastrophicWindowMs
		);
		if (
			this.recentFailures.length >=
			this.reconnect.catastrophicFailuresThreshold
		) {
			this.state = 'catastrophic';
			this.emit('publisher.recovery_state', {
				from: 'restarting',
				to: 'catastrophic',
				recent_failures: this.recentFailures.length,
				window_ms: this.reconnect.catastrophicWindowMs,
			});
			this.cleanupLocalPeer();
			// Best-effort DELETE — we're giving up either way.
			if (this.sessionUrl !== null) {
				const url = this.sessionUrl;
				this.sessionUrl = null;
				fetch(url, { method: 'DELETE' }).catch(() => {});
			}
			this.conf.onError?.(
				`catastrophic: ${this.recentFailures.length} failures within ${
					this.reconnect.catastrophicWindowMs / 1000
				}s, giving up`
			);
			return;
		}

		// Tear down local peer immediately so we stop processing its events.
		this.cleanupLocalPeer();

		// Await the DELETE of the old WHIP session with a timeout. This is the
		// load-bearing fix for the "closing existing publisher" kick: by
		// waiting for MediaMTX to acknowledge the DELETE (or timing out) before
		// posting a new offer, the path goes cleanly notReady → ready instead
		// of having the new POST race in and force-kick the old session.
		await this.deleteSessionAwait();

		// Re-check state in case close() kicked in during the await. We cast
		// through `string` because TS narrowed `this.state` from the entry
		// guard and refuses to widen it back, but at runtime close() can
		// reassign the property while we awaited.
		const stateAfterAwait = this.state as string;
		if (
			stateAfterAwait === 'closed' ||
			stateAfterAwait === 'catastrophic'
		) {
			return;
		}

		this.state = 'restarting';
		const backoff =
			this.reconnect.backoffMs[
				Math.min(this.attemptIndex, this.reconnect.backoffMs.length - 1)
			];
		this.attemptIndex++;
		this.emit('publisher.recovery_state', {
			from: stateAfterAwait,
			to: 'restarting',
			attempt_index: this.attemptIndex,
			backoff_ms: backoff,
		});

		this.restartTimeout = window.setTimeout(() => {
			this.restartTimeout = null;
			if (this.state !== 'restarting') {
				return;
			}
			this.state = 'running';
			this.start();
		}, backoff);

		this.conf.onError?.(
			`${error}, retrying in ${backoff}ms (attempt ${this.attemptIndex})`
		);
	}

	private cleanupLocalPeer() {
		if (this.pc !== null) {
			this.pc.close();
			this.pc = null;
		}
		this.offerData = null;
		this.queuedCandidates = [];
	}

	private async deleteSessionAwait(): Promise<void> {
		if (this.sessionUrl === null) {
			return;
		}
		const url = this.sessionUrl;
		this.sessionUrl = null;
		const mediamtxSessionId = this.mediamtxSessionId;
		this.mediamtxSessionId = null;
		this.emit('publisher.whip_delete', {
			phase: 'sent',
			mediamtx_session_id: mediamtxSessionId,
		});
		const startedAt = Date.now();
		try {
			const ctrl = new AbortController();
			const timer = window.setTimeout(
				() => ctrl.abort(),
				this.reconnect.deleteTimeoutMs
			);
			const response = await fetch(url, {
				method: 'DELETE',
				signal: ctrl.signal,
			});
			clearTimeout(timer);
			this.emit('publisher.whip_delete', {
				phase: 'response',
				status: response.status,
				duration_ms: Date.now() - startedAt,
				timed_out: false,
				mediamtx_session_id: mediamtxSessionId,
			});
		} catch {
			// Best effort: on abort/timeout/network error we proceed anyway —
			// the new POST will still succeed; MediaMTX will fall back to the
			// "closing existing publisher" path in that case (same as today),
			// but we tried.
			this.emit('publisher.whip_delete', {
				phase: 'response',
				status: null,
				duration_ms: Date.now() - startedAt,
				timed_out: true,
				mediamtx_session_id: mediamtxSessionId,
			});
		}
	}

	private authHeader(): Record<string, string> {
		if (this.conf.user !== undefined && this.conf.user !== '') {
			return {
				Authorization: `Basic ${btoa(
					`${this.conf.user}:${this.conf.pass ?? ''}`
				)}`,
			};
		}

		if (this.conf.token !== undefined && this.conf.token !== '') {
			return { Authorization: `Bearer ${this.conf.token}` };
		}

		return {};
	}

	private requestIceServers(): Promise<RTCIceServer[]> {
		return fetch(this.conf.url, {
			method: 'OPTIONS',
			headers: {
				...this.authHeader(),
			},
		}).then((response) =>
			MediaMTXWebRTCPublisher.linkToIceServers(
				response.headers.get('Link')
			)
		);
	}

	private setupPeerConnection(iceServers: RTCIceServer[]): Promise<string> {
		if (this.state !== 'running') {
			throw new Error('closed');
		}

		this.pc = new RTCPeerConnection({
			iceServers,
			sdpSemantics: 'unified-plan',
		} as RTCConfiguration);

		this.pc.onicecandidate = (event) => this.onLocalCandidate(event);
		this.pc.onconnectionstatechange = () => this.onConnectionState();

		this.conf.stream.getTracks().forEach((track) => {
			this.pc?.addTrack(track, this.conf.stream);
		});

		return this.pc.createOffer().then((offer) => {
			this.offerData = MediaMTXWebRTCPublisher.parseOffer(
				offer.sdp ?? ''
			);

			return this.pc
				?.setLocalDescription(offer)
				.then(() => offer.sdp ?? '') as Promise<string>;
		});
	}

	private sendOffer(offer: string): Promise<string> {
		if (this.state !== 'running') {
			throw new Error('closed');
		}

		const editedOffer = MediaMTXWebRTCPublisher.editOffer(
			offer,
			this.conf.videoCodec,
			this.conf.audioCodec,
			this.conf.audioBitrate,
			this.conf.audioVoice
		);

		this.emit('publisher.whip_post', { phase: 'sent' });
		const startedAt = Date.now();
		return fetch(this.conf.url, {
			method: 'POST',
			headers: {
				...this.authHeader(),
				'Content-Type': 'application/sdp',
			},
			body: editedOffer,
		}).then((response) => {
			const durationMs = Date.now() - startedAt;
			const mediamtxSessionId = response.headers.get('ID');

			if (response.status === 400) {
				return response.json().then((body) => {
					this.emit('publisher.whip_post', {
						phase: 'response',
						status: 400,
						duration_ms: durationMs,
						mediamtx_session_id: mediamtxSessionId,
						error: body.error,
					});
					throw new Error(body.error);
				});
			}

			if (response.status !== 201) {
				this.emit('publisher.whip_post', {
					phase: 'response',
					status: response.status,
					duration_ms: durationMs,
					mediamtx_session_id: mediamtxSessionId,
				});
				throw new Error(`bad status code ${response.status}`);
			}

			const location = response.headers.get('location');
			if (location === null) {
				this.emit('publisher.whip_post', {
					phase: 'response',
					status: response.status,
					duration_ms: durationMs,
					mediamtx_session_id: mediamtxSessionId,
					error: 'missing session location',
				});
				throw new Error('missing session location');
			}

			this.sessionUrl = new URL(location, this.conf.url).toString();
			this.mediamtxSessionId = mediamtxSessionId;
			this.emit('publisher.whip_post', {
				phase: 'response',
				status: 201,
				duration_ms: durationMs,
				mediamtx_session_id: mediamtxSessionId,
			});

			return response.text();
		});
	}

	private setAnswer(answer: string): Promise<void> {
		if (this.state !== 'running' || this.pc === null) {
			throw new Error('closed');
		}

		const editedAnswer = MediaMTXWebRTCPublisher.editAnswer(
			answer,
			this.conf.videoBitrate
		);

		return this.pc
			.setRemoteDescription(
				new RTCSessionDescription({
					type: 'answer',
					sdp: editedAnswer,
				})
			)
			.then(() => {
				if (this.state !== 'running') {
					return;
				}

				if (this.queuedCandidates.length !== 0) {
					this.sendLocalCandidates(this.queuedCandidates);
					this.queuedCandidates = [];
				}
			});
	}

	private onLocalCandidate(event: RTCPeerConnectionIceEvent) {
		if (this.state !== 'running' || event.candidate === null) {
			return;
		}

		if (this.sessionUrl === null) {
			this.queuedCandidates.push(event.candidate);
			return;
		}

		this.sendLocalCandidates([event.candidate]);
	}

	private sendLocalCandidates(candidates: RTCIceCandidate[]) {
		if (this.sessionUrl === null || this.offerData === null) {
			return;
		}

		fetch(this.sessionUrl, {
			method: 'PATCH',
			headers: {
				'Content-Type': 'application/trickle-ice-sdpfrag',
				'If-Match': '*',
			},
			body: MediaMTXWebRTCPublisher.generateSdpFragment(
				this.offerData,
				candidates
			),
		})
			.then((response) => {
				if (response.status === 204) {
					return;
				}

				if (response.status === 404) {
					throw new Error('stream not found');
				}

				throw new Error(`bad status code ${response.status}`);
			})
			.catch((error) => {
				this.handleError(error.toString());
			});
	}

	private onConnectionState() {
		// Bail out of terminal states. We may receive stray events on the
		// outgoing peer while a restart is in flight; ignore them.
		const currentState = this.state as string;
		if (
			currentState === 'closed' ||
			currentState === 'catastrophic' ||
			this.pc === null
		) {
			return;
		}

		const cs = this.pc.connectionState;

		if (cs === 'connected') {
			this.handleConnected();
			return;
		}

		if (cs === 'disconnected') {
			this.handleDisconnected();
			return;
		}

		if (cs === 'failed' || cs === 'closed') {
			this.handleError(`peer connection ${cs}`);
			return;
		}

		// 'new' / 'connecting' — nothing to do.
	}

	private handleConnected() {
		this.emit('publisher.peer_state', {
			from: this.lastPeerConnectionState,
			to: 'connected',
		});
		this.lastPeerConnectionState = 'connected';

		// Cancel any pending hysteresis: we've recovered before it fired.
		if (this.hysteresisTimeout !== null) {
			clearTimeout(this.hysteresisTimeout);
			this.hysteresisTimeout = null;
		}

		// Return to running (covers the hysteresis → connected edge).
		const previousState = this.state as string;
		this.state = 'running';
		if (previousState !== 'running') {
			this.emit('publisher.recovery_state', {
				from: previousState,
				to: 'running',
				attempt_index: this.attemptIndex,
			});
		}

		// Arm an attempt-counter reset: if we hold 'connected' for the
		// threshold duration, treat the cluster of recent failures as
		// resolved and start the next backoff from index 0. Use a
		// timer-based reset (rather than checking elapsed time on each
		// failure) so a steady stream of brief blips can't accidentally
		// reset the budget mid-storm.
		if (this.connectedResetTimeout !== null) {
			clearTimeout(this.connectedResetTimeout);
		}
		this.connectedResetTimeout = window.setTimeout(() => {
			this.connectedResetTimeout = null;
			this.attemptIndex = 0;
			this.recentFailures = [];
		}, this.reconnect.connectedResetThresholdMs);

		this.startStatsSampler();
		this.conf.onConnected?.();
	}

	private handleDisconnected() {
		this.emit('publisher.peer_state', {
			from: this.lastPeerConnectionState,
			to: 'disconnected',
		});
		this.lastPeerConnectionState = 'disconnected';

		this.stopStatsSampler();

		// Only react if we're in the normal running state. If we're already
		// in hysteresis, restarting, etc, ignore.
		if (this.state !== 'running') {
			return;
		}

		this.state = 'hysteresis';
		this.emit('publisher.recovery_state', {
			from: 'running',
			to: 'hysteresis',
		});

		// Cancel the connected-reset timer — we no longer have a steady
		// 'connected' to reset against.
		if (this.connectedResetTimeout !== null) {
			clearTimeout(this.connectedResetTimeout);
			this.connectedResetTimeout = null;
		}

		// Start the hysteresis timer. If we transition back to 'connected'
		// within the window, handleConnected cancels this. If we transition
		// to 'failed', handleError cancels this. Otherwise: time's up,
		// promote to a real failure.
		if (this.hysteresisTimeout !== null) {
			clearTimeout(this.hysteresisTimeout);
		}
		this.hysteresisTimeout = window.setTimeout(() => {
			this.hysteresisTimeout = null;
			if ((this.state as string) === 'hysteresis') {
				this.handleError('peer stuck in disconnected');
			}
		}, this.reconnect.disconnectedHysteresisMs);
	}

	private startStatsSampler() {
		this.stopStatsSampler();
		const intervalMs = this.conf.statsSampleIntervalMs ?? 10000;
		this.statsSamplerInterval = window.setInterval(() => {
			this.sampleStats();
		}, intervalMs);
	}

	private stopStatsSampler() {
		if (this.statsSamplerInterval !== null) {
			clearInterval(this.statsSamplerInterval);
			this.statsSamplerInterval = null;
		}
	}

	private async sampleStats() {
		const pc = this.pc;
		if (pc === null) {
			return;
		}
		try {
			const report = await pc.getStats();
			const snapshot = this.summarizeStats(report);
			this.emit('stats_snapshot', snapshot);
		} catch {
			// Drop sampling errors silently — non-critical.
		}
	}

	private summarizeStats(report: RTCStatsReport): Record<string, unknown> {
		let bytesSent = 0;
		let packetsSent = 0;
		let packetsLost = 0;
		let nackCount = 0;
		let pliCount = 0;
		let firCount = 0;
		let jitterMs: number | null = null;
		let rttMs: number | null = null;
		let candidateType: string | null = null;
		let candidateProtocol: string | null = null;

		// Build a quick lookup so we can resolve the selected candidate-pair's
		// local-candidate type without iterating twice.
		const byId = new Map<string, Record<string, unknown>>();
		report.forEach((stat: Record<string, unknown>) => {
			if (typeof stat.id === 'string') byId.set(stat.id, stat);
		});

		report.forEach((stat: Record<string, unknown>) => {
			const type = stat.type as string | undefined;
			if (type === 'outbound-rtp') {
				bytesSent += Number(stat.bytesSent ?? 0);
				packetsSent += Number(stat.packetsSent ?? 0);
				nackCount += Number(stat.nackCount ?? 0);
				pliCount += Number(stat.pliCount ?? 0);
				firCount += Number(stat.firCount ?? 0);
			} else if (type === 'remote-inbound-rtp') {
				const lost = Number(stat.packetsLost ?? 0);
				if (lost > packetsLost) packetsLost = lost;
				const j = stat.jitter;
				if (typeof j === 'number') {
					jitterMs = Math.round(j * 1000);
				}
			} else if (
				type === 'candidate-pair' &&
				stat.nominated === true &&
				stat.state === 'succeeded'
			) {
				const rtt = stat.currentRoundTripTime;
				if (typeof rtt === 'number') rttMs = Math.round(rtt * 1000);
				const localCandidateId = stat.localCandidateId;
				if (typeof localCandidateId === 'string') {
					const local = byId.get(localCandidateId);
					if (local) {
						candidateType = (local.candidateType as string) ?? null;
						candidateProtocol = (local.protocol as string) ?? null;
					}
				}
			}
		});

		return {
			bytes_sent: bytesSent,
			packets_sent: packetsSent,
			packets_lost: packetsLost,
			nack_count: nackCount,
			pli_count: pliCount,
			fir_count: firCount,
			jitter_ms: jitterMs,
			current_rtt_ms: rttMs,
			selected_candidate_type: candidateType,
			selected_candidate_protocol: candidateProtocol,
		};
	}
}
111;
